import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  confirmResetPassword,
  confirmSignIn,
  isAuthConfigured,
  resetPassword,
  signIn,
} from "../lib/auth.js";
import { formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";

type SignInLike = Awaited<ReturnType<typeof signIn>>;

type Step =
  | { kind: "credentials" }
  | { kind: "new_password" }
  | { kind: "totp" }
  | { kind: "totp_setup"; setupUri: string; secret: string }
  | { kind: "forgot" }
  | { kind: "reset"; email: string };

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnPrimary =
  "w-full rounded-md bg-(--accent) px-4 py-2.5 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) focus:outline-none focus:ring-2 focus:ring-(--accent) disabled:opacity-50";

const btnGhost =
  "text-xs text-gray-500 underline hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300";

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Sign-in failed. Please try again.";
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-orange-50 p-4 dark:bg-stone-950">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {children}
      </div>
    </div>
  );
}

function Heading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="text-center">
      <img src="/brand/life-logger-mark.png" alt="" className="mx-auto mb-3 h-12 w-12" />
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
    </div>
  );
}

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  useDocumentTitle(formatPageTitle("Sign in"));
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const autoSubmittedCode = useRef<string | null>(null);

  async function routeNextStep(output: SignInLike | Awaited<ReturnType<typeof confirmSignIn>>) {
    if (output.isSignedIn || output.nextStep.signInStep === "DONE") {
      onSignedIn();
      return;
    }
    autoSubmittedCode.current = null;
    setCode("");
    const next = output.nextStep.signInStep;
    if (next === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setNewPw("");
      setNewPw2("");
      setStep({ kind: "new_password" });
      return;
    }
    if (next === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
      setStep({ kind: "totp" });
      return;
    }
    if (next === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
      const details = output.nextStep.totpSetupDetails;
      setStep({
        kind: "totp_setup",
        secret: details.sharedSecret,
        setupUri: details.getSetupUri("Life Logger", email.trim()).toString(),
      });
      return;
    }
    setError(`Unsupported sign-in step: ${next}`);
  }

  async function handleCredentials(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const output = await signIn({ username: email.trim(), password });
      await routeNextStep(output);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    if (newPw.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (newPw !== newPw2) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const output = await confirmSignIn({ challengeResponse: newPw });
      await routeNextStep(output);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleTotp(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const output = await confirmSignIn({ challengeResponse: code.trim() });
      await routeNextStep(output);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function backToCredentials() {
    setStep({ kind: "credentials" });
    setCode("");
    setNewPw("");
    setNewPw2("");
    setError(null);
    autoSubmittedCode.current = null;
  }

  function goToForgotPassword() {
    setError(null);
    setResetSent(false);
    setCode("");
    setNewPw("");
    setNewPw2("");
    setStep({ kind: "forgot" });
  }

  async function handleForgot(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError("Enter your email.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword({ username: email.trim() });
      setStep({ kind: "reset", email: email.trim() });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(e: FormEvent) {
    e.preventDefault();
    if (step.kind !== "reset") return;
    if (!code.trim()) {
      setError("Enter the code sent to your email.");
      return;
    }
    if (newPw.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (newPw !== newPw2) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: step.email,
        confirmationCode: code.trim(),
        newPassword: newPw,
      });
      setResetSent(true);
      setPassword("");
      setStep({ kind: "credentials" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleResendCode() {
    if (step.kind !== "reset") return;
    setBusy(true);
    setError(null);
    try {
      await resetPassword({ username: step.email });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) return;
    if (busy || autoSubmittedCode.current === trimmed) return;
    if (step.kind === "totp" || step.kind === "totp_setup") {
      autoSubmittedCode.current = trimmed;
      void handleTotp();
    }
    // handleTotp is recreated each render; autoSubmittedCode guards double-submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, busy, step.kind]);

  if (!isAuthConfigured()) {
    return (
      <Card>
        <Heading
          title="Life Logger"
          subtitle="Cognito is not configured. Run scripts/deploy.ps1 so web/.env.local is generated."
        />
      </Card>
    );
  }

  if (step.kind === "credentials") {
    return (
      <Card>
        <form onSubmit={handleCredentials} className="space-y-5">
          <Heading title="Life Logger" subtitle="Sign in with your account." />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="username email"
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
              <button type="button" onClick={goToForgotPassword} className={btnGhost}>
                Forgot password?
              </button>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className={inputCls}
            />
          </div>
          {resetSent && !error && (
            <p className="text-sm text-green-600 dark:text-green-400">
              Password reset. Sign in with your new password.
            </p>
          )}
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-center text-xs text-gray-400 dark:text-gray-600">
            Access is by invitation only.
          </p>
        </form>
      </Card>
    );
  }

  if (step.kind === "new_password") {
    return (
      <Card>
        <form onSubmit={handleNewPassword} className="space-y-5">
          <Heading
            title="Set Your Password"
            subtitle="Choose a permanent password to finish setting up your account."
          />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoFocus
              autoComplete="new-password"
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm password</label>
            <input
              type="password"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              autoComplete="new-password"
              className={inputCls}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters, with uppercase, lowercase, a number, and a symbol.
          </p>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Saving…" : "Set password & continue"}
          </button>
          <div className="text-center">
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  if (step.kind === "forgot") {
    return (
      <Card>
        <form onSubmit={handleForgot} className="space-y-5">
          <Heading title="Reset Your Password" subtitle="Enter your email and we'll send you a reset code." />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="username email"
              className={inputCls}
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Sending…" : "Send reset code"}
          </button>
          <div className="text-center">
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  if (step.kind === "reset") {
    return (
      <Card>
        <form onSubmit={handleReset} className="space-y-5">
          <Heading title="Enter Reset Code" subtitle={`We sent a code to ${step.email}.`} />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reset code</label>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              autoComplete="one-time-code"
              placeholder="000000"
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm password</label>
            <input
              type="password"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              autoComplete="new-password"
              className={inputCls}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            At least 12 characters, with uppercase, lowercase, a number, and a symbol.
          </p>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button type="submit" disabled={busy} className={btnPrimary}>
            {busy ? "Resetting…" : "Reset password"}
          </button>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => void handleResendCode()} disabled={busy} className={btnGhost}>
              Resend code
            </button>
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  if (step.kind === "totp") {
    return (
      <Card>
        <form onSubmit={handleTotp} className="space-y-5">
          <Heading
            title="Two-Factor Authentication"
            subtitle="Enter the 6-digit code from your authenticator app."
          />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Authentication code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              autoComplete="one-time-code"
              placeholder="000000"
              className={inputCls}
            />
          </div>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <button type="submit" disabled={busy || code.trim().length !== 6} className={btnPrimary}>
            {busy ? "Verifying…" : "Verify"}
          </button>
          <div className="text-center">
            <button type="button" onClick={backToCredentials} className={btnGhost}>
              ← Back to sign in
            </button>
          </div>
        </form>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleTotp} className="space-y-5">
        <Heading
          title="Set Up Two-Factor Authentication"
          subtitle="Add Life Logger to your authenticator app, then enter a code."
        />
        <p className="text-center text-sm text-gray-600 dark:text-gray-300">
          On a phone?{" "}
          <a href={step.setupUri} className="text-(--accent) underline">
            Tap to open in your authenticator app
          </a>
        </p>
        <details className="rounded-md border border-gray-200 text-sm dark:border-gray-700">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
            Can't scan? Enter the setup key manually
          </summary>
          <div className="space-y-2 px-3 pb-3 pt-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              In your app choose <strong>Enter setup key</strong>. Account name:{" "}
              <em>Life Logger</em>. Type: <em>Time based</em>.
            </p>
            <div className="rounded bg-gray-100 px-3 py-2 dark:bg-gray-800">
              <span className="block break-all font-mono text-xs text-gray-900 select-all dark:text-gray-100">
                {step.secret}
              </span>
            </div>
          </div>
        </details>
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Verification code (from your app)
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            autoComplete="one-time-code"
            placeholder="000000"
            className={inputCls}
          />
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <button type="submit" disabled={busy || code.trim().length !== 6} className={btnPrimary}>
          {busy ? "Activating…" : "Activate & Sign in"}
        </button>
        <div className="text-center">
          <button type="button" onClick={backToCredentials} className={btnGhost}>
            ← Back to sign in
          </button>
        </div>
      </form>
    </Card>
  );
}
